import styled, { css } from 'styled-components';
import { getColorWithAlpha } from '@elfsight/lib-utils';
import { Deactivation2 } from '@elfsight/widget-deactivation';
import { getBreakpointsValues } from '@elfsight/widget-ui';
import useMergedRef from '@react-hook/merged-ref';
import { useSettings } from '@wu/widget-core';
import { useRect } from 'react-use-rect';
import { meta } from '../meta';
import { DesktopLayout } from './layouts/desktop-layout';
import type { Layout } from './layouts/layout-context';
import { useLayout } from './layouts/layout-context';
import { MobileLayout } from './layouts/mobile-layout';
import { TabletLayout } from './layouts/tablet-layout';
import { useInitWidgetAPIEvent } from './utils/use-init-widget-api-event';
import { useWidgetContext } from './widget-context';

const DESKTOP_BREAKPOINT = 810;
const TABLET_BREAKPOINT = 600;

export function Widget() {
  const { setLayout, layout } = useLayout();
  const { setWidgetWidth, widgetRef } = useWidgetContext();

  const [ref] = useRect(
    ({ width }) => {
      setLayout(
        getBreakpointsValues<Layout>(width, {
          [DESKTOP_BREAKPOINT]: 'desktop',
          [TABLET_BREAKPOINT]: 'tablet',
          0: 'mobile'
        })
      );
      if (width) {
        setWidgetWidth(width);
      }
    },
    { resize: true }
  );

  const { backgroundColor, textColor, font, renderThumbnailMode, ...settings } =
    useSettings();

  const containerRef = useInitWidgetAPIEvent();
  const mergedRef = useMergedRef(ref, containerRef, widgetRef);

  return (
    <Deactivation2
      childrenRef={mergedRef}
      appAlias={meta.appAlias}
      {...settings}
    >
      <Container
        _renderThumbnailMode={renderThumbnailMode}
        _backgroundColor={backgroundColor}
        _color={textColor}
        _font={font?.value}
      >
        {layout &&
          {
            desktop: <DesktopLayout />,
            tablet: <TabletLayout />,
            mobile: <MobileLayout />
          }[layout]}
      </Container>
    </Deactivation2>
  );
}

const Container = styled.div<{
  _renderThumbnailMode?: boolean;
  _backgroundColor: string;
  _color: string;
  _font?: string;
}>`
  ${({ _renderThumbnailMode }) =>
    _renderThumbnailMode
      ? css`
          width: 960px;
          height: 960px;
        `
      : css`
          width: 100%;
          height: 100dvh;
        `}

  ${({ _font }) => !!_font && `font-family: ${_font}`};
  -webkit-font-smoothing: antialiased;
  background: ${({ _backgroundColor }) => _backgroundColor};
  box-sizing: border-box;
  position: relative;
  z-index: 1;

  overflow: hidden;

  &:after {
    position: absolute;
    content: '';
    inset: 0;
    z-index: 99999999999;
    pointer-events: none;
    border: 1px solid ${({ _color }) => getColorWithAlpha(_color, 0.1)};
  }
`;
